import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Pagination from '../../components/common/Pagination';
import { 
  MessageCircle, Search, CheckCircle2, AlertCircle, MessageSquare, 
  Clock, ArrowLeft, Eye, Trash2, RefreshCw, ChevronRight, ShieldAlert,
  MapPin, BookOpen, Mic, FileText, RotateCcw, ChevronDown, X, Layers
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  answered: { label: 'Answered', color: 'bg-amber-100 text-amber-700', icon: MessageSquare },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

// Topic Tree Dropdown Component
const TopicTreeDropdown = ({ topics, selectedTopic, onSelect, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  // Find selected topic name
  const selectedTopicName = useMemo(() => {
    if (!selectedTopic) return null;
    for (const parent of topics) {
      if (parent.id === selectedTopic) return parent.name;
      for (const child of (parent.children || [])) {
        if (child.id === selectedTopic) return child.name;
      }
    }
    return null;
  }, [topics, selectedTopic]);
  
  // Filter topics based on search
  const filteredTopics = useMemo(() => {
    if (!search.trim()) return topics;
    const searchLower = search.toLowerCase();
    const filtered = [];
    
    for (const parent of topics) {
      const parentMatches = parent.name.toLowerCase().includes(searchLower);
      const matchingChildren = (parent.children || []).filter(c => 
        c.name.toLowerCase().includes(searchLower)
      );
      
      if (parentMatches || matchingChildren.length > 0) {
        filtered.push({
          ...parent,
          children: parentMatches ? parent.children : matchingChildren
        });
      }
    }
    return filtered;
  }, [topics, search]);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  if (disabled || topics.length === 0) return null;
  
  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          selectedTopic 
            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        <Layers className="w-4 h-4" />
        <span>{selectedTopicName || 'Select Topic'}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {selectedTopic && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(''); }}
            className="ml-1 hover:bg-emerald-200 rounded-full p-0.5"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white border rounded-lg shadow-lg">
          {/* Search */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                autoFocus
              />
            </div>
          </div>
          
          {/* Topic Tree */}
          <div className="max-h-64 overflow-y-auto p-1">
            {/* All Topics option */}
            <button
              onClick={() => { onSelect(''); setIsOpen(false); setSearch(''); }}
              className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
                !selectedTopic ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
              }`}
            >
              All Topics
            </button>
            
            {filteredTopics.length === 0 && search ? (
              <p className="text-sm text-gray-500 text-center py-4">No topics found</p>
            ) : (
              filteredTopics.map(parent => (
                <div key={parent.id}>
                  {/* Parent Topic */}
                  <button
                    onClick={() => { onSelect(parent.id); setIsOpen(false); setSearch(''); }}
                    className={`w-full text-left px-3 py-2 rounded text-sm font-medium hover:bg-gray-100 flex items-center justify-between ${
                      selectedTopic === parent.id ? 'bg-emerald-50 text-emerald-700' : 'text-gray-800'
                    }`}
                  >
                    <span>{parent.name}</span>
                    {parent.count > 0 && (
                      <Badge variant="outline" className="text-xs">{parent.count}</Badge>
                    )}
                  </button>
                  
                  {/* Child Topics */}
                  {parent.children?.map(child => (
                    <button
                      key={child.id}
                      onClick={() => { onSelect(child.id); setIsOpen(false); setSearch(''); }}
                      className={`w-full text-left pl-8 pr-3 py-1.5 rounded text-sm hover:bg-gray-100 flex items-center justify-between ${
                        selectedTopic === child.id ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600'
                      }`}
                    >
                      <span>{child.name}</span>
                      {child.count > 0 && (
                        <Badge variant="outline" className="text-xs">{child.count}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Filter Pill Component
const FilterPill = ({ label, count, isActive, onClick, icon: Icon, variant = 'default' }) => {
  const baseClasses = "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap";
  
  const variantClasses = {
    default: isActive 
      ? 'bg-[#0A7EA4] text-white shadow-sm' 
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    grade: isActive 
      ? 'bg-indigo-600 text-white shadow-sm' 
      : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    mmd: isActive 
      ? 'bg-purple-600 text-white shadow-sm' 
      : 'bg-purple-50 text-purple-700 hover:bg-purple-100',
    function: isActive 
      ? 'bg-teal-600 text-white shadow-sm' 
      : 'bg-teal-50 text-teal-700 hover:bg-teal-100',
    status: isActive 
      ? 'bg-gray-800 text-white shadow-sm' 
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  };
  
  return (
    <button 
      className={`${baseClasses} ${variantClasses[variant]}`}
      onClick={onClick}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
      {count !== undefined && count !== null && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
          isActive ? 'bg-white/20' : 'bg-gray-200 text-gray-600'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
};

const ForumManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Data state
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ grades: [], mmd_locations: [], functions: [] });
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filter state
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedMmd, setSelectedMmd] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedFunction, setSelectedFunction] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, questionId: null, title: '' });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Check if any filter is active
  const hasActiveFilters = selectedGrade || selectedMmd || selectedFunction || selectedTopic || selectedStatus || searchQuery;

  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = {};
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedMmd) params.mmd_location = selectedMmd;
      if (selectedFunction) params.function_id = selectedFunction;
      
      const response = await axios.get(`${API}/forum/admin/filter-options`, { params });
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, [selectedGrade, selectedMmd, selectedFunction]);

  const fetchTopics = useCallback(async () => {
    if (!selectedFunction) {
      setTopics([]);
      return;
    }
    try {
      const params = { function_id: selectedFunction };
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedMmd) params.mmd_location = selectedMmd;
      
      const response = await axios.get(`${API}/forum/admin/topics`, { params });
      setTopics(response.data.topics || []);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [selectedFunction, selectedGrade, selectedMmd]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedMmd) params.mmd_location = selectedMmd;
      if (selectedFunction) params.function_id = selectedFunction;
      
      const response = await axios.get(`${API}/forum/admin/stats`, { params });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [selectedGrade, selectedMmd, selectedFunction]);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: itemsPerPage };
      if (selectedGrade) params.grade = selectedGrade;
      if (selectedMmd) params.mmd_location = selectedMmd;
      if (selectedFunction) params.function_id = selectedFunction;
      if (selectedTopic) params.topic_id = selectedTopic;
      if (selectedStatus) params.status = selectedStatus;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API}/forum/admin/questions`, { params });
      setQuestions(response.data.questions);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, selectedGrade, selectedMmd, selectedFunction, selectedTopic, selectedStatus, searchQuery]);

  const fetchPendingVerification = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/forum/admin/pending-verification`);
      setPendingQuestions(response.data.questions);
    } catch (error) {
      console.error('Failed to fetch pending:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When filters change, refetch filter options and stats
  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
  }, [selectedGrade, selectedMmd, selectedFunction, fetchFilterOptions, fetchStats]);

  // When function changes, fetch topics and clear topic selection
  useEffect(() => {
    fetchTopics();
    setSelectedTopic('');
  }, [selectedFunction, fetchTopics]);

  // When filters or search change, refetch questions
  useEffect(() => {
    if (activeTab === 'all') {
      fetchQuestions();
    } else if (activeTab === 'pending') {
      fetchPendingVerification();
    }
  }, [activeTab, fetchQuestions, fetchPendingVerification]);

  const handleVerify = async (answerId) => {
    try {
      await axios.post(`${API}/forum/answers/${answerId}/verify`);
      toast.success('Answer verified');
      fetchStats();
      if (activeTab === 'pending') {
        fetchPendingVerification();
      } else {
        fetchQuestions();
      }
    } catch (error) {
      console.error('Failed to verify answer:', error);
      toast.error('Failed to verify answer');
    }
  };

  const handleDelete = async (questionId) => {
    try {
      await axios.delete(`${API}/forum/admin/questions/${questionId}`);
      toast.success('Question deleted permanently');
      setDeleteDialog({ open: false, questionId: null, title: '' });
      fetchStats();
      fetchFilterOptions();
      fetchQuestions();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      setProcessing(true);
      await axios.post(`${API}/forum/admin/bulk-delete`, { question_ids: selectedIds });
      toast.success(`Deleted ${selectedIds.length} questions permanently`);
      setSelectedIds([]);
      setBulkDeleteDialog(false);
      fetchStats();
      fetchFilterOptions();
      fetchQuestions();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete questions');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === questions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(questions.map(q => q.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const resetFilters = () => {
    setSelectedGrade('');
    setSelectedMmd('');
    setSelectedFunction('');
    setSelectedTopic('');
    setSelectedStatus('');
    setSearchQuery('');
    setPage(1);
  };

  const refreshAll = () => {
    fetchFilterOptions();
    fetchStats();
    fetchQuestions();
  };

  // Calculate total count for "All" options
  const totalGradeCount = useMemo(() => 
    filterOptions.grades?.reduce((sum, g) => sum + g.count, 0) || 0
  , [filterOptions.grades]);

  // Total MMD count includes specific MMDs + general questions
  const totalMmdCount = useMemo(() => {
    const specificMmdCount = filterOptions.mmd_locations?.reduce((sum, m) => sum + m.count, 0) || 0;
    const generalCount = filterOptions.general_questions_count || 0;
    return specificMmdCount + generalCount;
  }, [filterOptions.mmd_locations, filterOptions.general_questions_count]);

  const totalFunctionCount = useMemo(() => 
    filterOptions.functions?.reduce((sum, f) => sum + f.count, 0) || 0
  , [filterOptions.functions]);

  return (
    <Layout>
      <div className="page-transition max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0A7EA4]/10 rounded-lg">
                <MessageCircle className="w-6 h-6 text-[#0A7EA4]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Forum Management</h1>
                <p className="text-sm text-gray-600">Moderate questions and verify answers</p>
              </div>
            </div>
            <Button onClick={refreshAll} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Card>
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-gray-900">{stats.total_questions}</div>
                <div className="text-xs text-gray-500">Total</div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-blue-700">{stats.open_questions}</div>
                <div className="text-xs text-blue-600">Open</div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-amber-700">{stats.answered_questions}</div>
                <div className="text-xs text-amber-600">Answered</div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-green-700">{stats.verified_questions}</div>
                <div className="text-xs text-green-600">Verified</div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-bold text-orange-700">{stats.pending_verification}</div>
                  {stats.pending_verification > 0 && (
                    <ShieldAlert className="w-5 h-5 text-orange-500 animate-pulse" />
                  )}
                </div>
                <div className="text-xs text-orange-600">Pending</div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-purple-700">{stats.total_answers}</div>
                <div className="text-xs text-purple-600">Answers</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Section */}
        <Card className="mb-4 sticky top-0 z-10 bg-white shadow-sm">
          <CardContent className="p-4 space-y-3">
            {/* Line 1: Grade Pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Grade:</span>
              <FilterPill
                label="All"
                count={totalGradeCount}
                isActive={!selectedGrade}
                onClick={() => { setSelectedGrade(''); setPage(1); }}
                variant="grade"
              />
              {filterOptions.grades?.map(grade => (
                <FilterPill
                  key={grade.name}
                  label={grade.name}
                  count={grade.count}
                  isActive={selectedGrade === grade.name}
                  onClick={() => { setSelectedGrade(grade.name); setPage(1); }}
                  variant="grade"
                />
              ))}
            </div>

            {/* Line 2: MMD Location Pills - Only shows specific MMDs when questions exist */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Location:</span>
              <FilterPill
                label="All"
                count={totalMmdCount}
                isActive={!selectedMmd}
                onClick={() => { setSelectedMmd(''); setPage(1); }}
                icon={MapPin}
                variant="mmd"
              />
              {/* Only render specific MMD pills - no "All MMD" data pill */}
              {filterOptions.mmd_locations?.map(mmd => (
                <FilterPill
                  key={mmd.id}
                  label={mmd.name}
                  count={mmd.count}
                  isActive={selectedMmd === mmd.id}
                  onClick={() => { setSelectedMmd(mmd.id); setPage(1); }}
                  icon={MapPin}
                  variant="mmd"
                />
              ))}
            </div>

            {/* Line 3: Function Pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Function:</span>
              <FilterPill
                label="All"
                count={totalFunctionCount}
                isActive={!selectedFunction}
                onClick={() => { setSelectedFunction(''); setPage(1); }}
                icon={BookOpen}
                variant="function"
              />
              {filterOptions.functions?.map(func => (
                <FilterPill
                  key={func.id}
                  label={`F${func.function_number}${func.function_suffix ? '-' + func.function_suffix : ''} ${func.name}${func.exam_type === 'written' ? ' (W)' : ''}`}
                  count={func.count}
                  isActive={selectedFunction === func.id}
                  onClick={() => { setSelectedFunction(func.id); setPage(1); }}
                  icon={func.exam_type === 'written' ? FileText : Mic}
                  variant="function"
                />
              ))}
            </div>

            {/* Line 4: Status + Search + Reset */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Status:</span>
              <div className="flex items-center gap-2">
                <FilterPill
                  label="All"
                  isActive={!selectedStatus}
                  onClick={() => { setSelectedStatus(''); setPage(1); }}
                  variant="status"
                />
                <FilterPill
                  label="Open"
                  isActive={selectedStatus === 'open'}
                  onClick={() => { setSelectedStatus('open'); setPage(1); }}
                  icon={AlertCircle}
                  variant="status"
                />
                <FilterPill
                  label="Answered"
                  isActive={selectedStatus === 'answered'}
                  onClick={() => { setSelectedStatus('answered'); setPage(1); }}
                  icon={MessageSquare}
                  variant="status"
                />
                <FilterPill
                  label="Verified"
                  isActive={selectedStatus === 'verified'}
                  onClick={() => { setSelectedStatus('verified'); setPage(1); }}
                  icon={CheckCircle2}
                  variant="status"
                />
              </div>
              
              {/* Topic Dropdown - Only shows when a function is selected */}
              {selectedFunction && topics.length > 0 && (
                <TopicTreeDropdown
                  topics={topics}
                  selectedTopic={selectedTopic}
                  onSelect={(topicId) => { setSelectedTopic(topicId); setPage(1); }}
                  disabled={!selectedFunction}
                />
              )}
              
              <div className="flex-1 min-w-[200px] max-w-md ml-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-10 h-9"
                  />
                </div>
              </div>
              
              {hasActiveFilters && (
                <Button variant="ghost" onClick={resetFilters} size="sm" className="text-gray-500">
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              All Questions
              <Badge variant="outline">{total}</Badge>
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Verification
              {stats?.pending_verification > 0 && (
                <Badge className="bg-orange-500 text-white">{stats.pending_verification}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && activeTab === 'all' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-red-700">
              {selectedIds.length} question(s) selected
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
                Clear Selection
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setBulkDeleteDialog(true)}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Selected
              </Button>
            </div>
          </div>
        )}

        {/* Questions List */}
        {activeTab === 'all' && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : questions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No questions found</h3>
                  <p className="text-gray-500">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'No questions in the forum yet'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <Checkbox
                    checked={selectedIds.length === questions.length && questions.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-sm text-gray-500">Select all ({total} total)</span>
                </div>
                
                <div className="space-y-3">
                  {questions.map((question) => {
                    const statusConfig = STATUS_CONFIG[question.status];
                    const StatusIcon = statusConfig?.icon || AlertCircle;
                    
                    return (
                      <Card 
                        key={question.id}
                        className={`${selectedIds.includes(question.id) ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedIds.includes(question.id)}
                              onCheckedChange={() => toggleSelect(question.id)}
                              className="mt-1"
                            />
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge className={statusConfig?.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig?.label}
                                </Badge>
                                <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                                  {question.grade}
                                </Badge>
                                {/* MMD Badge - Show "All MMD" if no specific MMD, otherwise show MMD name */}
                                <Badge variant="outline" className="bg-purple-50 text-purple-700">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {question.mmd_location && question.mmd_location !== 'None' 
                                    ? question.mmd_location.replace(' MMD', '') 
                                    : 'All MMD'}
                                </Badge>
                                {question.function_name && (
                                  <Badge variant="outline" className="bg-teal-50 text-teal-700">
                                    {question.function_name}
                                  </Badge>
                                )}
                                {/* Topic Badge */}
                                {question.topic_name && (
                                  <Badge variant="outline" className="bg-gray-100 text-gray-700">
                                    <BookOpen className="w-3 h-3 mr-1" />
                                    {question.topic_name}
                                  </Badge>
                                )}
                              </div>
                              
                              <h3 
                                className="font-semibold text-gray-900 mb-1 cursor-pointer hover:text-[#0A7EA4]"
                                onClick={() => navigate(`/forum/question/${question.id}?from=admin`)}
                              >
                                {question.title}
                              </h3>
                              
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>by {question.user_name}</span>
                                <span>{formatDistanceToNow(new Date(question.created_at), { addSuffix: true })}</span>
                                <span>{question.answer_count} answers</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/forum/question/${question.id}?from=admin`)}
                                title="View question"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteDialog({ 
                                  open: true, 
                                  questionId: question.id, 
                                  title: question.title 
                                })}
                                title="Delete permanently"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pagination */}
                {total > 0 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={page}
                      totalItems={total}
                      itemsPerPage={itemsPerPage}
                      onPageChange={(newPage) => setPage(newPage)}
                      onItemsPerPageChange={(newSize) => {
                        setItemsPerPage(newSize);
                        setPage(1);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Pending Verification Tab */}
        {activeTab === 'pending' && (
          <>
            {pendingQuestions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">All caught up!</h3>
                  <p className="text-gray-500">No answers pending verification</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingQuestions.map((question) => (
                  <Card key={question.id} className="border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className="bg-amber-100 text-amber-700">
                              {question.unverified_answers} unverified answer(s)
                            </Badge>
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                              {question.grade}
                            </Badge>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700">
                              <MapPin className="w-3 h-3 mr-1" />
                              {question.mmd_location && question.mmd_location !== 'None' 
                                ? question.mmd_location.replace(' MMD', '') 
                                : 'All MMD'}
                            </Badge>
                          </div>
                          <h3 
                            className="font-semibold text-gray-900 cursor-pointer hover:text-[#0A7EA4]"
                            onClick={() => navigate(`/forum/question/${question.id}?from=admin`)}
                          >
                            {question.title}
                          </h3>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/forum/question/${question.id}?from=admin`)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View & Verify
                        </Button>
                      </div>
                      
                      {/* Show unverified answers */}
                      <div className="space-y-2 mt-3 border-t pt-3">
                        {question.unverified_answers_list?.slice(0, 2).map((answer) => (
                          <div 
                            key={answer.id} 
                            className="flex items-start justify-between p-2 bg-gray-50 rounded"
                          >
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 line-clamp-2">{answer.content}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                by {answer.user_name} • {formatDistanceToNow(new Date(answer.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 ml-3"
                              onClick={() => handleVerify(answer.id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Verify
                            </Button>
                          </div>
                        ))}
                        {question.unverified_answers > 2 && (
                          <p className="text-xs text-gray-500 text-center">
                            +{question.unverified_answers - 2} more answers to review
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, questionId: null, title: '' })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this question permanently?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-medium text-gray-900 block mb-2">&ldquo;{deleteDialog.title}&rdquo;</span>
                This action cannot be undone. The question and all its answers will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleDelete(deleteDialog.questionId)}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.length} questions permanently?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. All selected questions and their answers will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleBulkDelete} 
                disabled={processing}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ForumManagementPage;
